/** Métricas derivadas de um upload — tempo de fila, tempo de
 *  processamento, vazão.
 *
 *  Mora fora do componente porque é aritmética pura: dá pra testar sem
 *  montar tela, e o cálculo é o que tem chance de estar errado (o
 *  render não).
 *
 *  A separação entre ESPERAR e PROCESSAR é o ponto. "O upload levou 4
 *  minutos" não diz nada acionável; "esperou 3m40s na fila e processou
 *  em 20s" diz que o gargalo é worker parado, não linha lenta. */

import type { UploadSummary } from "@/shared/api/api-client";

/** O backend guarda UTC numa coluna SEM fuso (`DateTime` naive), então
 *  o JSON chega como `2026-08-04T12:34:56` — sem `Z`. E o JS, por
 *  especificação, lê data-e-hora sem fuso como hora LOCAL. Sem esse
 *  `Z` explícito, um upload que começou agora apareceria com 3 horas
 *  de duração no horário de Brasília. */
export function parseServerTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = Date.parse(hasZone ? value : `${value}Z`);
  return Number.isNaN(parsed) ? null : parsed;
}

export type UploadTiming = {
  /** Da chegada até o worker pegar. Nulo enquanto ninguém pegou. */
  queueSeconds: number | null;
  /** Do início até o fim. Enquanto processa, conta até AGORA. */
  runSeconds: number | null;
  /** `true` quando `runSeconds` está correndo (ainda não terminou). */
  running: boolean;
  /** Linhas por segundo de processamento. Nulo sem base pra dividir. */
  rowsPerSecond: number | null;
};

export function uploadTiming(upload: UploadSummary, now: number): UploadTiming {
  const uploaded = parseServerTime(upload.uploaded_at);
  const started = parseServerTime(upload.started_at);
  const finished = parseServerTime(upload.finished_at);

  const queueSeconds =
    uploaded !== null && started !== null ? Math.max(0, (started - uploaded) / 1000) : null;

  const running = started !== null && finished === null;
  // `Math.max(0, …)` porque no modo ao vivo o fim é o relógio do
  // NAVEGADOR contra um início do SERVIDOR: relógios fora de sincronia
  // dariam negativo, e "-2s" na tela é pior que "0s".
  const runSeconds =
    started === null ? null : Math.max(0, ((finished ?? now) - started) / 1000);

  const rowsPerSecond =
    runSeconds !== null && runSeconds > 0 && upload.processed_rows > 0
      ? upload.processed_rows / runSeconds
      : null;

  return { queueSeconds, runSeconds, running, rowsPerSecond };
}

/** Duração legível. Segundos abaixo de um minuto, minuto+segundo até
 *  uma hora, hora+minuto acima disso — precisão de segundo em algo que
 *  durou duas horas é ruído. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 1) return "menos de 1s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return rest === 0 ? `${minutes}min` : `${minutes}min ${rest}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}min`;
}

/** Vazão. Vira linhas/min abaixo de 1 linha/s porque "0,3 linha/s" se
 *  lê pior que "18 linhas/min" pra descrever a mesma lentidão. */
export function formatThroughput(rowsPerSecond: number | null): string {
  if (rowsPerSecond === null) return "—";
  if (rowsPerSecond < 1) return `${Math.round(rowsPerSecond * 60)} linhas/min`;
  return `${rowsPerSecond.toFixed(1).replace(".", ",")} linhas/s`;
}

// Construído UMA vez, no módulo: o custo do `Intl` é o construtor, não
// o `.format`, e esta função roda por linha da lista de uploads.
//
// **Mês NUMÉRICO, diferente do `formatDateTime` compartilhado**, que
// escreve "12 de set.". Não está claro se a diferença foi escolha ou
// acaso — mudar altera o que a tela de uploads mostra, então fica como
// está até alguém decidir.
const dataHora = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
});

const diaCompleto = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
});
const horaMinuto = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit"
});

/** "DD/MM/AAAA às HH:MM" no relógio de quem lê. Carimbo de verdade (UTC do
 *  servidor), como `formatMoment` — nunca leitura de hora de parede. */
export function formatMomentFull(value: string | null | undefined): string {
  const parsed = parseServerTime(value);
  if (parsed === null) return "—";
  const moment = new Date(parsed);
  return `${diaCompleto.format(moment)} às ${horaMinuto.format(moment)}`;
}

/** Data e hora local, curta. O servidor manda UTC; quem lê pensa no
 *  próprio relógio. */
export function formatMoment(value: string | null | undefined): string {
  const parsed = parseServerTime(value);
  if (parsed === null) return "—";
  return dataHora.format(new Date(parsed));
}
